import React, { useEffect, useState } from "react";
import api from "../api";
import { Search as SearchIcon, FilterX, Plus, AlertTriangle } from "lucide-react";
import Autocomplete from "../components/Autocomplete";
import Section from "../components/FilterSection";

const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("it-IT").format(n));

const STATUS_OPTIONS = [
  { value: "ATTIVA", label: "Attiva" },
  { value: "CESSATA", label: "Cessata" },
  { value: "REGISTRATA", label: "Registrata" },
  { value: "INATTIVA", label: "Inattiva" },
  { value: "SOSPESA", label: "Sospesa" },
  { value: "IN_ISCRIZIONE", label: "In iscrizione" },
];

export default function Ricerca() {
  const [filters, setFilters] = useState({
    provincia: "", comune: "", companyName: "", atecoCode: "",
    activityStatus: "ATTIVA", revenueMin: "", revenueMax: "",
    employeesMin: "", employeesMax: "", pec: "", legalForm: "",
    sdiCode: "", cciaa: "", rea: "", ownerTaxCode: "",
    creationDateFrom: "", creationDateTo: "",
    lat: "", lng: "", radius: "",
    _dataEnrichment: "address",  // default: 5x cheaper than "start"
  });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isMock, setIsMock] = useState(false);
  const [mockReason, setMockReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selected, setSelected] = useState({});
  const [selectedAllAcrossPages, setSelectedAllAcrossPages] = useState(false);
  const [selectedAllIds, setSelectedAllIds] = useState([]);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState("");
  const [pageCost, setPageCost] = useState(null);
  const [showAddList, setShowAddList] = useState(false);
  const [lists, setLists] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [provinceList, setProvinceList] = useState([]);
  const [legalForms, setLegalForms] = useState([]);

  // Per-result OpenAPI cost in € (must match backend OPENAPI_PRICES table)
  const ENRICHMENT_PRICES = {
    name:        { eur: 0.001, label: "Solo nome (€0.001/risultato)" },
    address:     { eur: 0.010, label: "Indirizzo + città/provincia (€0.010/risultato) ⭐ consigliato" },
    pec:         { eur: 0.030, label: "PEC ufficiale (€0.030/risultato)" },
    start:       { eur: 0.050, label: "Completo: sito/telefono/fatturato/dipendenti (€0.050/risultato)" },
    advanced:    { eur: 0.100, label: "Avanzato: tutti i dati (€0.100/risultato)" },
  };
  const enrichmentEur = ENRICHMENT_PRICES[filters._dataEnrichment]?.eur || 0.010;
  const estimatedPageCost = (0.010 + 50 * enrichmentEur).toFixed(3);

  useEffect(() => {
    api.get("/lookup/province").then(r => {
      setProvinceList(r.data.map(p => ({
        value: p.sigla, label: `${p.nome} (${p.sigla})`, subtitle: p.regione
      })));
    });
    api.get("/lookup/legalforms").then(r => {
      const data = r.data?.data || [];
      setLegalForms(data.map(l => ({ value: l.code, label: l.description })));
    }).catch(() => {});
  }, []);

  const upd = (k, v) => setFilters(f => ({...f, [k]: v}));

  const fetchComuni = async (q) => {
    if (q.length < 1 && !filters.provincia) return [];
    const { data } = await api.get("/lookup/comuni", { params: { q, provincia: filters.provincia, limit: 30 }});
    return data.map(c => ({
      value: c.codiceBelfiore,
      label: `${c.nome} (${c.sigla})`,
      subtitle: `Belfiore ${c.codiceBelfiore} · ${c.regione}`,
    }));
  };

  const runSearch = async (newSkip = 0) => {
    setLoading(true); setError("");
    try {
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_,v])=>v!=="" && v!=null));
      const { data } = await api.post("/companies/search", { filters: cleanFilters, skip: newSkip, limit: 50 });
      setResults(data.companies || []);
      setTotal(data.total ?? null);
      setHasMore(!!data.hasMore);
      setIsMock(data.mock || false);
      setMockReason(data.mockReason || "");
      setPageCost(data.pageCostEur ?? null);
      setSkip(newSkip);
      setSelected({});
      setSelectedAllAcrossPages(false);
      setSelectedAllIds([]);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore ricerca");
    } finally { setLoading(false); }
  };

  const selectAllAcrossPages = async () => {
    const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_,v])=>v!=="" && v!=null));
    const maxResults = 200;
    const estCost = (10 * 0.010 + maxResults * enrichmentEur).toFixed(2);
    const ok = window.confirm(
      `⚠️ ATTENZIONE COSTO ⚠️\n\n` +
      `Stai per scaricare fino a ${maxResults} aziende paginando OpenAPI.it.\n\n` +
      `Profondità dati: ${ENRICHMENT_PRICES[filters._dataEnrichment]?.label || filters._dataEnrichment}\n` +
      `💰 COSTO MASSIMO STIMATO: €${estCost}\n\n` +
      `Procedere?`
    );
    if (!ok) return;
    setSelectingAll(true); setError("");
    try {
      const { data } = await api.post("/companies/search/all", { filters: cleanFilters, maxResults });
      const ids = data.companyIds || [];
      setSelectedAllIds(ids);
      setSelectedAllAcrossPages(true);
      setSelected(Object.fromEntries(ids.map(id => [id, true])));
      const actualCost = (data.totalCostEur || 0).toFixed(2);
      let msg = `Selezionate ${ids.length} aziende. Costo reale OpenAPI: €${actualCost}.`;
      if (data.capped) {
        msg += `\n\nLimite massimo (${maxResults}) raggiunto. Affina i filtri se ne servono di più.`;
      }
      alert(msg);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore selezione totale");
    } finally { setSelectingAll(false); }
  };

  const toggle = (id) => setSelected(s => ({...s, [id]: !s[id]}));
  const selectedIds = selectedAllAcrossPages ? selectedAllIds : Object.keys(selected).filter(k => selected[k]);
  const allSelected = results.length > 0 && results.every(r => selected[r.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(results.map(r => [r.id, true])));
  };

  const openAddToList = async () => {
    const { data } = await api.get("/lists");
    setLists(data);
    setShowAddList(true);
  };

  const addToList = async (listId) => {
    setLoading(true);
    try {
      const count = selectedIds.length;
      await api.post(`/lists/${listId}/items`, { companyIds: selectedIds });
      setShowAddList(false);
      setSelected({});
      setSelectedAllAcrossPages(false);
      setSelectedAllIds([]);
      alert(`${count} aziende aggiunte alla lista.\n\nL'arricchimento (email/sito/titolari) NON è automatico: usa il pulsante "Arricchisci" nel CRM o nella lista per recuperarli solo quando serve, risparmiando crediti OpenAPI.`);
    } finally { setLoading(false); }
  };

  const createAndAdd = async () => {
    if (!newListName.trim()) return;
    setLoading(true);
    try {
      const count = selectedIds.length;
      const { data: lst } = await api.post("/lists", { name: newListName.trim() });
      await api.post(`/lists/${lst.id}/items`, { companyIds: selectedIds });
      setShowAddList(false); setSelected({}); setSelectedAllAcrossPages(false); setSelectedAllIds([]); setNewListName("");
      alert(`Lista "${lst.name}" creata con ${count} aziende.\n\nVai sul CRM o sulla lista e usa "Scraping siti GRATIS" (zero crediti) o "Arricchimento OpenAPI" (a pagamento) per recuperare email e titolari.`);
    } finally { setLoading(false); }
  };

  const reset = () => setFilters(Object.fromEntries(Object.keys(filters).map(k => {
    if (k === "activityStatus") return [k, "ATTIVA"];
    if (k === "_dataEnrichment") return [k, "address"];
    return [k, ""];
  })));

  return (
    <div data-testid="ricerca-page">
      <div className="h-display mb-2">Ricerca aziende</div>
      <div className="text-muted mb-6">Filtri combinabili su Company API OpenAPI.it · 107 province · 7.904 comuni con codice Belfiore</div>

      {isMock && (
        <div className="card mb-4" style={{borderLeft:"4px solid var(--mv-warning)",padding:"14px 18px",background:"var(--mv-warning-bg)"}} data-testid="mock-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} color="var(--mv-warning)"/>
            <div>
              <div style={{fontWeight:700,color:"#8a4a14"}}>Modalità DEMO</div>
              <div className="text-small" style={{color:"#8a4a14"}}>
                Dati di esempio in uso. Motivo: <strong>{mockReason || "API non raggiungibile"}</strong>.
                Verifica le credenziali OpenAPI in <a href="/settings" style={{color:"var(--mv-primary)",fontWeight:600}}>Impostazioni → Stato integrazioni</a>.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6" style={{gridTemplateColumns:"340px 1fr"}}>
        <div className="card" data-testid="search-filters">
          <Section title="Geografici">
            <label className="label">Provincia</label>
            <Autocomplete
              value={filters.provincia}
              onChange={(v) => { upd("provincia", v); upd("comune", ""); }}
              fetcher={(q) => Promise.resolve(
                provinceList.filter(p => !q || p.label.toLowerCase().includes(q.toLowerCase())).slice(0, 20)
              )}
              initialOptions={provinceList.slice(0, 20)}
              placeholder="Cerca provincia (es. Milano)"
              testid="filter-provincia"
            />
            <div style={{height:10}} />
            <label className="label">Comune (codice Belfiore)</label>
            <Autocomplete
              value={filters.comune}
              onChange={(v) => upd("comune", v)}
              fetcher={fetchComuni}
              placeholder="Inizia a digitare il nome del comune"
              testid="filter-comune"
              minChars={2}
            />

            <div className="text-small text-muted mt-4 mb-2" style={{fontWeight:600}}>Ricerca geo-spaziale (opzionale)</div>
            <div className="grid gap-2" style={{gridTemplateColumns:"1fr 1fr"}}>
              <input className="input" placeholder="Lat" value={filters.lat} onChange={e=>upd("lat",e.target.value)} />
              <input className="input" placeholder="Lng" value={filters.lng} onChange={e=>upd("lng",e.target.value)} />
            </div>
            <input className="input mt-2" placeholder="Raggio (km)" type="number" value={filters.radius} onChange={e=>upd("radius",e.target.value)} />
          </Section>

          <Section title="Identità">
            <label className="label">Ragione sociale (supporta *)</label>
            <input className="input" placeholder="Es. dentist*" value={filters.companyName} onChange={e=>upd("companyName",e.target.value)} data-testid="filter-name" />
            <div style={{height:10}} />
            <label className="label">Codice ATECO</label>
            <input className="input" placeholder="Es. 86.23.00" value={filters.atecoCode} onChange={e=>upd("atecoCode",e.target.value)} data-testid="filter-ateco" />
            <div style={{height:10}} />
            <label className="label">Stato attività</label>
            <select className="select" value={filters.activityStatus} onChange={e=>upd("activityStatus",e.target.value)} data-testid="filter-status">
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              <option value="">Tutte</option>
            </select>
            <div style={{height:10}} />
            <label className="label">PEC presente</label>
            <select className="select" value={filters.pec} onChange={e=>upd("pec",e.target.value)} data-testid="filter-pec">
              <option value="">Indifferente</option>
              <option value="true">Sì</option>
              <option value="false">No</option>
            </select>
            {legalForms.length > 0 && (
              <>
                <div style={{height:10}} />
                <label className="label">Natura giuridica</label>
                <select className="select" value={filters.legalForm} onChange={e=>upd("legalForm",e.target.value)}>
                  <option value="">Tutte</option>
                  {legalForms.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </>
            )}
          </Section>

          <Section title="Economici" defaultOpen={false}>
            <div className="grid gap-2" style={{gridTemplateColumns:"1fr 1fr"}}>
              <div><label className="label">Fatturato min (€)</label>
                <input type="number" className="input" value={filters.revenueMin} onChange={e=>upd("revenueMin",e.target.value)} data-testid="filter-rev-min" /></div>
              <div><label className="label">Fatturato max (€)</label>
                <input type="number" className="input" value={filters.revenueMax} onChange={e=>upd("revenueMax",e.target.value)} data-testid="filter-rev-max" /></div>
              <div><label className="label">Dipendenti min</label>
                <input type="number" className="input" value={filters.employeesMin} onChange={e=>upd("employeesMin",e.target.value)} data-testid="filter-emp-min" /></div>
              <div><label className="label">Dipendenti max</label>
                <input type="number" className="input" value={filters.employeesMax} onChange={e=>upd("employeesMax",e.target.value)} data-testid="filter-emp-max" /></div>
            </div>
          </Section>

          <Section title="Codici fiscali e camerali" defaultOpen={false}>
            <label className="label">Codice SDI</label>
            <input className="input" value={filters.sdiCode} onChange={e=>upd("sdiCode",e.target.value)} />
            <div style={{height:10}}/>
            <label className="label">CCIAA</label>
            <input className="input" placeholder="Es. MI" value={filters.cciaa} onChange={e=>upd("cciaa",e.target.value.toUpperCase())} />
            <div style={{height:10}}/>
            <label className="label">REA</label>
            <input className="input" value={filters.rea} onChange={e=>upd("rea",e.target.value)} />
            <div style={{height:10}}/>
            <label className="label">CF proprietario</label>
            <input className="input" value={filters.ownerTaxCode} onChange={e=>upd("ownerTaxCode",e.target.value.toUpperCase())} />
          </Section>

          <Section title="Temporali" defaultOpen={false}>
            <label className="label">Data creazione dal</label>
            <input type="date" className="input" value={filters.creationDateFrom} onChange={e=>upd("creationDateFrom",e.target.value)} />
            <div style={{height:10}}/>
            <label className="label">Data creazione al</label>
            <input type="date" className="input" value={filters.creationDateTo} onChange={e=>upd("creationDateTo",e.target.value)} />
          </Section>

          <Section title="💰 Costo & profondità dati" defaultOpen={true}>
            <label className="label">Quanti dati restituire (per risultato)</label>
            <select className="select" value={filters._dataEnrichment} onChange={e=>upd("_dataEnrichment", e.target.value)} data-testid="filter-enrichment">
              <option value="name">Solo nome (€0.001/risultato)</option>
              <option value="address">Indirizzo + città/provincia (€0.010/risultato) ⭐ consigliato</option>
              <option value="pec">PEC ufficiale (€0.030/risultato)</option>
              <option value="start">Completo: sito/telefono/fatturato/dipendenti (€0.050/risultato)</option>
              <option value="advanced">Avanzato (€0.100/risultato)</option>
            </select>
            <div className="text-small text-muted mt-2" data-testid="cost-preview">
              💸 Stima per pagina (50 risultati): <strong>€{estimatedPageCost}</strong><br/>
              {filters._dataEnrichment === "address" && filters.activityStatus && (
                <span style={{color:"var(--mv-warning)"}}>
                  ⚠️ Con enrichment "Indirizzo" il filtro <strong>Stato attività</strong> non viene applicato dal server (OpenAPI non lo include nei dati base). Le aziende cessate potrebbero essere incluse. Usa "Completo" se ti serve filtrare le cessate.
                </span>
              )}
              {filters._dataEnrichment !== "address" && (
                <span>Per Discovery sito/email è meglio usare "Indirizzo" e arricchire dal CRM con "Trova siti + email GRATIS".</span>
              )}
            </div>
          </Section>

          <div className="flex gap-2 mt-4">
            <button onClick={()=>runSearch(0)} className="btn btn-primary" disabled={loading} data-testid="search-submit">
              {loading ? <span className="spinner" /> : <><SearchIcon size={16}/> Cerca (~€{estimatedPageCost})</>}
            </button>
            <button onClick={reset} className="btn btn-secondary" data-testid="search-reset"><FilterX size={16}/></button>
          </div>
          {pageCost != null && (
            <div className="text-small text-muted mt-2" data-testid="actual-cost" style={{padding:"6px 0"}}>
              💰 Costo reale ultima pagina: <strong>€{pageCost.toFixed(3)}</strong>
            </div>
          )}
          {error && <div className="badge badge-red mt-4" style={{display:"block",padding:8}}>{error}</div>}
        </div>

        <div>
          <div className="flex justify-between items-center mb-4" style={{gap:12,flexWrap:"wrap"}}>
            <div className="text-small text-muted" data-testid="search-counter">
              {results.length === 0 && !loading && "Nessuna ricerca eseguita"}
              {results.length > 0 && (
                total != null
                  ? `Trovate ${fmt(total)} aziende — selezionate ${selectedIds.length}`
                  : `Trovate almeno ${fmt(skip + results.length)}${hasMore ? "+" : ""} aziende — selezionate ${selectedIds.length}`
              )}
            </div>
            <div className="flex items-center gap-2">
              {results.length > 0 && (hasMore || skip > 0) && !selectedAllAcrossPages && (
                <button onClick={selectAllAcrossPages} className="btn btn-secondary btn-sm" disabled={selectingAll} data-testid="select-all-pages-btn">
                  {selectingAll ? <span className="spinner" /> : "Seleziona TUTTI i risultati"}
                </button>
              )}
              {selectedAllAcrossPages && (
                <div className="badge badge-green" style={{padding:"6px 10px"}} data-testid="all-selected-badge">
                  Selezionate tutte le {selectedAllIds.length} aziende su tutte le pagine
                </div>
              )}
              {selectedIds.length > 0 && (
                <button onClick={openAddToList} className="btn btn-primary btn-sm" data-testid="add-to-list-btn">
                  <Plus size={14}/> Aggiungi a lista ({selectedIds.length})
                </button>
              )}
            </div>
          </div>

          {results.length > 0 && (
            <div className="card" style={{padding:0,overflow:"hidden"}}>
              <table className="tbl" data-testid="search-results-table">
                <thead>
                  <tr>
                    <th style={{width:30}}><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-cb" /></th>
                    <th>Azienda</th>
                    <th>Comune</th>
                    <th>ATECO</th>
                    <th>Sito</th>
                    <th>Email</th>
                    <th>Fatturato</th>
                    <th>Dip.</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(c => (
                    <tr key={c.id} data-testid={`result-row-${c.vatCode}`}>
                      <td><input type="checkbox" checked={!!selected[c.id]} onChange={()=>toggle(c.id)} data-testid={`select-cb-${c.vatCode}`} /></td>
                      <td>
                        <div style={{fontWeight:600}}>{c.companyName}</div>
                        <div className="text-small text-muted">P.IVA {c.vatCode}</div>
                      </td>
                      <td>{c.city} ({c.province})</td>
                      <td className="text-small text-muted">{c.ateco}</td>
                      <td>{c.website ? <a href={`https://${c.website.replace(/^https?:\/\//,"")}`} target="_blank" rel="noreferrer">{c.website}</a> : <span className="text-muted">—</span>}</td>
                      <td className="text-small">{c.email || c.pec || <span className="text-muted">—</span>}</td>
                      <td>{c.revenue ? `€ ${fmt(c.revenue)}` : "—"}</td>
                      <td>{c.employees ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center" style={{padding:14}}>
                <button className="btn btn-secondary btn-sm" disabled={skip===0||loading} onClick={()=>runSearch(Math.max(0,skip-50))} data-testid="prev-page">← Precedenti</button>
                <div className="text-small text-muted">
                  {skip+1} – {skip+results.length}
                  {total != null ? ` di ${fmt(total)}` : (hasMore ? " (altri risultati disponibili)" : " (ultima pagina)")}
                </div>
                <button className="btn btn-secondary btn-sm" disabled={!hasMore||loading} onClick={()=>runSearch(skip+50)} data-testid="next-page">Successivi →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddList && (
        <div className="modal-backdrop" onClick={()=>setShowAddList(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} data-testid="add-list-modal">
            <div className="h-section mb-4">Aggiungi {selectedIds.length} aziende a una lista</div>
            <div className="text-small text-muted mb-2">Liste esistenti</div>
            <div style={{maxHeight:240,overflow:"auto",border:"1px solid var(--mv-border)",borderRadius:8,marginBottom:16}}>
              {lists.length === 0 && <div className="text-small text-muted" style={{padding:14}}>Nessuna lista. Creane una nuova qui sotto.</div>}
              {lists.map(l => (
                <div key={l.id} className="flex justify-between items-center" style={{padding:"10px 14px",borderBottom:"1px solid var(--mv-border)"}}>
                  <div>
                    <div style={{fontWeight:600}}>{l.name}</div>
                    <div className="text-small text-muted">{l.totalItems || 0} aziende</div>
                  </div>
                  <button onClick={()=>addToList(l.id)} className="btn btn-primary btn-sm" data-testid={`add-to-list-${l.id}`}>Aggiungi</button>
                </div>
              ))}
            </div>
            <div className="text-small text-muted mb-2">O crea una nuova lista</div>
            <div className="flex gap-2">
              <input className="input" placeholder='Es. "Dentisti Milano Q1"' value={newListName} onChange={e=>setNewListName(e.target.value)} data-testid="new-list-name" />
              <button onClick={createAndAdd} className="btn btn-primary" disabled={!newListName.trim()} data-testid="create-list-and-add">Crea</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
